"use client"

import emailjs from "emailjs-com"
import {monthNames} from "@/src/utils/emailHelper";

export interface ContactFormData {
  fullName: string
  phone: string
  premiseType: string
  otherPremiseType?: string
  buildingType: string
  needsDemolition: string
  demolitionDetails?: string
  hasDesignProject: string
  startDate: string
  endDate: string
  additionalComment: string
  priorities: string[]
}

const SERVICE_ID = process.env.NEXT_PUBLIC_EMAILJS_SERVICE_ID || ""
const TEMPLATE_ID = process.env.NEXT_PUBLIC_EMAILJS_TEMPLATE_ID || ""
const PUBLIC_KEY = process.env.NEXT_PUBLIC_EMAILJS_PUBLIC_KEY || ""

function formatDateUA(dateString: string): string {
  if (!dateString) return "Не вказано"
  const date = new Date(dateString)
  if (isNaN(date.getTime())) return "Невірна дата"

  const day = date.getDate()
  const month = monthNames[date.getMonth()]
  const year = date.getFullYear()

  return `${day} ${month} ${year}`
}

const getPremiseTypeLabel = (premiseType: string, otherType?: string): string => {
  const types: Record<string, string> = {
    apartment: "Квартира",
    apartments: "Апартаменти",
    house: "Приватний будинок",
    commercial: "Комерційне приміщення",
    other: `Інше: ${otherType || "не вказано"}`,
  }
  return types[premiseType] || premiseType
}

const getBuildingTypeLabel = (buildingType: string): string => {
  const types: Record<string, string> = {
    new: "Новобудова",
    old: "Старий житловий фонд",
  }
  return types[buildingType] || buildingType
}

const getDemolitionInfo = (needsDemolition: string, details?: string): string => {
  if (needsDemolition === "yes") {
    return `Так.\n Коментарі: ${details ? `${details}` : ""}`
  }
  return "Ні"
}

const getDesignProjectLabel = (hasDesignProject: string): string => {
  const labels: Record<string, string> = {
    yes: "Так",
    no: "Ні",
    inProgress: "У процесі розробки",
  }
  return labels[hasDesignProject] || hasDesignProject
}

const getPrioritiesLabel = (priorities: string[]): string => {
  const priorityLabels: Record<string, string> = {
    speed: "Швидкість виконання ремонту",
    price: "Ціна виконання ремонту",
    quality: "Висока якість виконання робіт",
  }

  if (!priorities || priorities.length === 0) return "Не вказано"

  return priorities
      .map((p) => `- ${priorityLabels[p] || p}`)
      .join("\n")
}

export async function sendEmailAction(formData: ContactFormData): Promise<{ success: boolean; message: string }> {
  try {
    const templateParams = {
      fullName: formData.fullName,
      phone: formData.phone,
      premiseType: getPremiseTypeLabel(formData.premiseType, formData.otherPremiseType),
      buildingType: getBuildingTypeLabel(formData.buildingType),
      demolitionDetails: getDemolitionInfo(formData.needsDemolition, formData.demolitionDetails),
      hasDesignProject: getDesignProjectLabel(formData.hasDesignProject),
      startDate: formatDateUA(formData.startDate),
      endDate: formatDateUA(formData.endDate),
      additionalComment: formData.additionalComment || "Без додаткових коментарів",
      priorities: getPrioritiesLabel(formData.priorities),
    }

    await emailjs.send(SERVICE_ID, TEMPLATE_ID, templateParams, PUBLIC_KEY)

    return {
      success: true,
      message: "Заявка успішно відправлена. Ми зв'яжемося з вами найближчим часом.",
    }
  } catch (error: any) {
    console.error("Помилка відправки email:", error)
    return {
      success: false,
      message: "Не вдалося відправити заявку. Спробуйте ще раз.",
    }
  }
}